// Modal "Gerar simulado a partir deste caderno".
// [[project-caderno-bank-context]] [[project-token-tracking-billing]]
//
// Coleta os parâmetros (qtd questões, dificuldade, tipos, instruções extras)
// e chama POST /api/student/caderno/generate-bank. Backend faz batching +
// retry + JSON strict pra garantir N questões reais. Quota por plano roda
// ANTES da chamada — UI mostra mensagem amigável quando bloqueado.

import { useEffect, useState } from "preact/hooks";
import { api, type AiStudyDocument } from "./api";
import {
  FileText,
  Loader2,
  MessageSquare,
  Minimize2,
  Sparkles,
  Wand2,
  X
} from "lucide-react";
import { openBank } from "./BankDetailView";
import { setActiveNav } from "./App";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { Slider } from "@app/ui/components/slider";
import {
  bankGenModalOpen,
  bankGenTask,
  cancelBankGeneration,
  dismissBankGen,
  startBankGeneration
} from "./generateBankStore";

// Tipo de questão selecionável pelo aluno. "Misto" gera MC + Essay no
// mesmo banco com distribuição livre. Os outros 2 = puros.
type QuestionTypeChoice = "mc" | "essay" | "misto";
type DifficultyChoice = "facil" | "medio" | "dificil" | "misto";
// Intensidade da COBERTURA (substitui a quantidade exata). O nº de questões
// emerge do conteúdo: a IA mapeia tópicos por documento e gera por tópico.
type AmountIntensity = "enxuto" | "equilibrado" | "aprofundado";

/** Mini button group violeta-no-ativo. Mesma vibe da SegmentedButtons do
 *  AiPrefsSection — não importei direto pra evitar coupling com outra view. */
function ButtonChoice<T extends string>({
  value,
  options,
  onChange,
  cols
}: {
  value: T;
  options: Array<{ value: T; label: string; sub?: string }>;
  onChange: (v: T) => void;
  cols?: 2 | 3 | 4;
}) {
  const gridClass =
    cols === 2 ? "grid-cols-2" : cols === 4 ? "grid-cols-4" : "grid-cols-3";
  return (
    <div class={`grid ${gridClass} gap-2`}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active ? "true" : "false"}
            class={
              "flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left transition-colors " +
              (active
                ? "border-violet-500 bg-violet-500/15 text-violet-700 dark:text-violet-300"
                : "border-border bg-background hover:bg-accent")
            }
          >
            <span class="text-sm font-semibold">{opt.label}</span>
            {opt.sub && (
              <span class="text-[10px] font-normal text-muted-foreground">
                {opt.sub}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface Props {
  sessionId: string;
  sessionTitle: string;
  onClose: () => void;
}

type ScopeChoice = "all" | "docs_only" | "chat_only" | "custom";

export function GenerateBankModal({ sessionId, sessionTitle, onClose }: Props) {
  const [intensity, setIntensity] = useState<AmountIntensity>("equilibrado");
  // Alvo numérico OPCIONAL (vira o teto distribuído por documento no backend).
  const [useTarget, setUseTarget] = useState(false);
  const [targetQuestions, setTargetQuestions] = useState(30);
  // Consolidação OPCIONAL: reduz pra N por documento num passe de "editor".
  const [consolidate, setConsolidate] = useState(false);
  const [consolidatePerDoc, setConsolidatePerDoc] = useState(10);
  const [difficulty, setDifficulty] = useState<DifficultyChoice>("misto");
  const [questionType, setQuestionType] = useState<QuestionTypeChoice>("mc");
  const [extraInstructions, setExtraInstructions] = useState("");
  const [bankName, setBankName] = useState(`Simulado IA — ${sessionTitle}`);
  // Erro de validação/carregamento de docs (pré-envio). O erro da GERAÇÃO em si
  // vive no store (bankGenTask.error) pra sobreviver ao fechar o modal.
  const [err, setErr] = useState<string | null>(null);

  // Tarefa global de geração. O modal só reflete a tarefa DESTE caderno; se
  // houver uma rodando em outro, bloqueia novo disparo (uma por vez).
  const task = bankGenTask.value;
  const myTask = task && task.sessionId === sessionId ? task : null;
  const otherBusy = !!task && task.sessionId !== sessionId && task.status === "generating";

  // Avisa o dock/pill que o modal está aberto (pra ele não duplicar a UI).
  useEffect(() => {
    bankGenModalOpen.value = true;
    return () => {
      bankGenModalOpen.value = false;
    };
  }, []);

  // ============== Source picker + toggle conhecimento externo ==============
  // [[project-caderno-bank-context]]
  const [scope, setScope] = useState<ScopeChoice>("all");
  const [allowExternalKnowledge, setAllowExternalKnowledge] = useState(true);
  // Selected docs quando scope="custom". Set = todos os IDs que entram.
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [chatLastN, setChatLastN] = useState(30);
  const [includeFragmentsCustom, setIncludeFragmentsCustom] = useState(true);
  // Carrega docs/msgs do caderno só quando o aluno entra em "Customizar" — evita
  // requisição extra pros 3 presets simples. Cacheado pelo lifecycle do modal.
  const [docs, setDocs] = useState<AiStudyDocument[] | null>(null);
  const [messageCount, setMessageCount] = useState<number>(0);
  const [fragmentCount, setFragmentCount] = useState<number>(0);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (scope !== "custom" || docs !== null || loadingDetail) return;
    setLoadingDetail(true);
    api
      .getAiStudy(sessionId)
      .then((d) => {
        setDocs(d.documents);
        setMessageCount(d.messages.length);
        setFragmentCount(d.fragments?.length ?? 0);
        // Default: tudo selecionado quando entra em customizar.
        setSelectedDocIds(new Set(d.documents.map((x) => x.id)));
      })
      .catch((e) => setErr(friendlyError(e)))
      .finally(() => setLoadingDetail(false));
  }, [scope, sessionId, docs, loadingDetail]);

  function toggleDoc(id: string) {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Fechar = MINIMIZAR. A tarefa segue no store; o pill flutuante assume.
  // Nunca bloqueia o fechamento (o ponto da feature é poder sair).
  function close() {
    onClose();
  }

  // Dispara a geração no store (sobrevive ao fechar o modal) e mantém o modal
  // aberto mostrando o progresso — o aluno pode minimizar quando quiser.
  function generate() {
    if (otherBusy) return;
    setErr(null);
    const kinds: Array<"mc" | "essay"> =
      questionType === "mc"
        ? ["mc"]
        : questionType === "essay"
          ? ["essay"]
          : ["mc", "essay"];
    // Monta o body conforme o preset. "custom" envia listas explícitas; outros
    // deixam o backend traduzir o scope nos defaults certos.
    const customExtras =
      scope === "custom"
        ? {
            docIds: Array.from(selectedDocIds),
            chatLastN,
            includeFragments: includeFragmentsCustom
          }
        : {};
    // Estimativa só pra UI/teto — o número real emerge da cobertura no backend.
    // Se o aluno definiu um alvo numérico, ele vira o teto distribuído.
    const estimate = intensity === "enxuto" ? 20 : intensity === "equilibrado" ? 35 : 50;
    const effectiveTarget = useTarget ? targetQuestions : estimate;
    void startBankGeneration({
      sessionId,
      sessionTitle,
      numQuestions: effectiveTarget,
      payload: {
        sessionId,
        mode: "coverage",
        intensity,
        difficulty,
        kinds,
        extraInstructions: extraInstructions.trim() || undefined,
        bankName: bankName.trim() || undefined,
        scope,
        allowExternalKnowledge,
        ...(useTarget ? { targetQuestions } : {}),
        ...(consolidate ? { consolidatePerDoc } : {}),
        ...customExtras
      }
    });
  }

  function openCreated() {
    const id = myTask?.result?.bank.id;
    if (id) {
      // Bug 2026-05-25: chamar só openBank() seta o signal mas não navega
      // — usuário ficava preso na tela do caderno. Setar activeNav antes
      // garante que a tela troca pra /banks e o detalhe aparece.
      setActiveNav("bancos");
      openBank(id);
    }
    dismissBankGen();
    onClose();
  }

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border border-border bg-card shadow-2xl">
        <header class="flex items-center gap-2 border-b border-border px-5 py-3">
          <Wand2 className="size-5 text-violet-500" />
          <h2 class="flex-1 text-base font-bold">Gerar simulado a partir deste caderno</h2>
          <button
            type="button"
            onClick={close}
            aria-label={myTask?.status === "generating" ? "Minimizar" : "Fechar"}
            title={myTask?.status === "generating" ? "Minimizar (continua gerando)" : "Fechar"}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 overflow-y-auto px-5 py-4">
          {!myTask || myTask.status === "error" ? (
            <div class="space-y-4">
              <p class="text-[12px] text-muted-foreground">
                A IA vai criar um banco de questões NOVO usando o conteúdo
                deste caderno (documentos anexados, fragmentos, mensagens da
                conversa, bancos linkados) como referência. O banco é salvo
                como <strong>privado</strong>. Você pode mudar a
                visibilidade depois.
              </p>

              <label class="block">
                <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Nome do banco
                </span>
                <input
                  type="text"
                  value={bankName}
                  onInput={(e) => setBankName((e.target as HTMLInputElement).value)}
                  maxLength={200}
                  class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </label>

              <div class="space-y-2">
                <label class="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Quanto cobrir
                </label>
                <ButtonChoice<AmountIntensity>
                  value={intensity}
                  cols={3}
                  options={[
                    { value: "enxuto", label: "Enxuto", sub: "Só o essencial" },
                    { value: "equilibrado", label: "Equilibrado", sub: "Boa cobertura" },
                    { value: "aprofundado", label: "Aprofundado", sub: "Cobre tudo" }
                  ]}
                  onChange={(v) => setIntensity(v)}
                />
                <p class="text-[10px] text-muted-foreground">
                  Sem número fixo: a IA mapeia os tópicos de cada documento e
                  gera questões por tópico. A quantidade sai do conteúdo (o
                  plano pode limitar o teto).
                </p>

                {/* Alvo numérico opcional — vira o teto distribuído por documento */}
                <label class="flex cursor-pointer items-center gap-2 pt-1 text-[12px]">
                  <input
                    type="checkbox"
                    checked={useTarget}
                    onChange={() => setUseTarget((v) => !v)}
                    class="size-3.5 accent-violet-500"
                  />
                  <span>Definir um número aproximado de questões</span>
                </label>
                {useTarget && (
                  <div class="space-y-1 pl-1">
                    <div class="flex items-center justify-between">
                      <span class="text-[11px] font-medium">Alvo de questões</span>
                      <span class="rounded border border-border bg-background px-1.5 py-0.5 text-[11px] font-bold tabular-nums">
                        ~{targetQuestions}
                      </span>
                    </div>
                    <Slider
                      min={5}
                      max={100}
                      step={5}
                      value={[targetQuestions]}
                      onValueChange={(arr: number[]) => {
                        const v = Array.isArray(arr) ? arr[0] : null;
                        if (typeof v === "number") setTargetQuestions(v);
                      }}
                      aria-label="Alvo de questões"
                    />
                    <p class="text-[10px] text-muted-foreground">
                      Teto distribuído entre os documentos (não força o número
                      exato; o plano ainda pode limitar).
                    </p>
                  </div>
                )}

                {/* Consolidação opcional — reduz pra N por documento */}
                <label class="flex cursor-pointer items-center gap-2 pt-1 text-[12px]">
                  <input
                    type="checkbox"
                    checked={consolidate}
                    onChange={() => setConsolidate((v) => !v)}
                    class="size-3.5 accent-violet-500"
                  />
                  <span>Consolidar: manter só as melhores por documento</span>
                </label>
                {consolidate && (
                  <div class="space-y-1 pl-1">
                    <div class="flex items-center justify-between">
                      <span class="text-[11px] font-medium">Máximo por documento</span>
                      <span class="rounded border border-border bg-background px-1.5 py-0.5 text-[11px] font-bold tabular-nums">
                        {consolidatePerDoc}
                      </span>
                    </div>
                    <Slider
                      min={3}
                      max={20}
                      step={1}
                      value={[consolidatePerDoc]}
                      onValueChange={(arr: number[]) => {
                        const v = Array.isArray(arr) ? arr[0] : null;
                        if (typeof v === "number") setConsolidatePerDoc(v);
                      }}
                      aria-label="Máximo de questões por documento"
                    />
                    <p class="text-[10px] text-muted-foreground">
                      Depois de gerar, um passe extra funde redundâncias e
                      mantém as melhores de cada material (leva um pouco mais).
                    </p>
                  </div>
                )}
              </div>

              <div class="space-y-2">
                <label class="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Dificuldade
                </label>
                <ButtonChoice<DifficultyChoice>
                  value={difficulty}
                  cols={4}
                  options={[
                    { value: "facil", label: "Fácil", sub: "Fixação" },
                    { value: "medio", label: "Média", sub: "ENEM" },
                    { value: "dificil", label: "Difícil", sub: "FUVEST" },
                    { value: "misto", label: "Mista", sub: "Equilibrada" }
                  ]}
                  onChange={(v) => setDifficulty(v)}
                />
              </div>

              <div class="space-y-2">
                <label class="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Tipo de questão
                </label>
                <ButtonChoice<QuestionTypeChoice>
                  value={questionType}
                  cols={3}
                  options={[
                    { value: "mc", label: "Múltipla escolha", sub: "5 alternativas + gabarito" },
                    { value: "essay", label: "Dissertativa", sub: "Gabarito-modelo + critérios" },
                    { value: "misto", label: "Misto", sub: "MC + dissertativa" }
                  ]}
                  onChange={(v) => setQuestionType(v)}
                />
              </div>

              {/* ============== Material fonte (source picker) ============== */}
              <div class="space-y-2 rounded-md border border-border bg-background/50 p-3">
                <label class="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Material que a IA vai usar
                </label>
                <ButtonChoice<ScopeChoice>
                  value={scope}
                  cols={4}
                  options={[
                    { value: "all", label: "Tudo", sub: "Docs + chat" },
                    { value: "docs_only", label: "Só docs", sub: "Sem chat" },
                    { value: "chat_only", label: "Só chat", sub: "Sem docs" },
                    { value: "custom", label: "Customizar", sub: "Escolher" }
                  ]}
                  onChange={(v) => setScope(v)}
                />
                {scope === "custom" && (
                  <div class="mt-2 space-y-3 border-t border-border/50 pt-3">
                    {loadingDetail ? (
                      <div class="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <Loader2 className="size-3 animate-spin" />
                        Carregando documentos do caderno…
                      </div>
                    ) : docs && docs.length === 0 ? (
                      <p class="text-[11px] text-muted-foreground">
                        Este caderno ainda não tem documentos anexados.
                      </p>
                    ) : docs ? (
                      <div class="space-y-1">
                        <p class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                          Documentos ({selectedDocIds.size}/{docs.length})
                        </p>
                        <div class="max-h-44 space-y-1 overflow-y-auto">
                          {docs.map((d) => {
                            const checked = selectedDocIds.has(d.id);
                            return (
                              <label
                                key={d.id}
                                class="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 hover:bg-accent"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleDoc(d.id)}
                                  class="mt-0.5 size-3.5 accent-violet-500"
                                />
                                <FileText className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                                <span class="flex-1 truncate text-[12px]">{d.filename}</span>
                                <span class="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                                  {Math.max(1, Math.round(d.sizeBytes / 1024))} KB
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    {fragmentCount > 0 && (
                      <label class="flex items-center gap-2 text-[12px]">
                        <input
                          type="checkbox"
                          checked={includeFragmentsCustom}
                          onChange={() =>
                            setIncludeFragmentsCustom((v) => !v)
                          }
                          class="size-3.5 accent-violet-500"
                        />
                        <span>
                          Incluir <strong>{fragmentCount}</strong> fragmento
                          {fragmentCount === 1 ? "" : "s"} de apoio (redação)
                        </span>
                      </label>
                    )}

                    <div class="space-y-1">
                      <div class="flex items-center justify-between">
                        <span class="flex items-center gap-1.5 text-[11px] font-medium">
                          <MessageSquare className="size-3" />
                          Mensagens recentes do chat
                        </span>
                        <span class="rounded border border-border bg-background px-1.5 py-0.5 text-[11px] font-bold tabular-nums">
                          {chatLastN === 0 ? "nenhuma" : `últimas ${chatLastN}`}
                        </span>
                      </div>
                      <Slider
                        min={0}
                        max={Math.max(30, Math.min(100, messageCount || 30))}
                        step={5}
                        value={[chatLastN]}
                        onValueChange={(arr: number[]) => {
                          const v = Array.isArray(arr) ? arr[0] : null;
                          if (typeof v === "number") setChatLastN(v);
                        }}
                        aria-label="Mensagens do chat a incluir"
                      />
                      <p class="text-[10px] text-muted-foreground">
                        Total no caderno: {messageCount} mensagens. 0 = não usar chat.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* ============== Toggle conhecimento externo ============== */}
              <label class="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-background/50 p-3">
                <input
                  type="checkbox"
                  checked={allowExternalKnowledge}
                  onChange={() => setAllowExternalKnowledge((v) => !v)}
                  class="mt-0.5 size-4 accent-violet-500"
                />
                <div class="flex-1">
                  <span class="block text-[13px] font-medium">
                    Pode usar conhecimento externo
                  </span>
                  <span class="block text-[11px] text-muted-foreground">
                    {allowExternalKnowledge ? (
                      <>
                        IA usa o material como base e complementa com conhecimento
                        geral quando precisa. Recomendado pra cadernos pequenos.
                      </>
                    ) : (
                      <>
                        <strong>Modo estrito:</strong> IA usa SOMENTE o material
                        marcado acima. Se faltar conteúdo, gera menos questões e
                        avisa o que não conseguiu cobrir.
                      </>
                    )}
                  </span>
                </div>
              </label>

              <label class="block">
                <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Instruções extras (opcional)
                </span>
                <textarea
                  value={extraInstructions}
                  onInput={(e) =>
                    setExtraInstructions((e.target as HTMLTextAreaElement).value)
                  }
                  maxLength={2000}
                  rows={3}
                  placeholder='Ex: "Foca em casos clínicos de cardiologia", "Inclui questões com gráficos", "Estilo banca FUVEST 2024"'
                  class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </label>

              {(err || myTask?.error) && (
                <div class="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
                  {err ?? myTask?.error}
                </div>
              )}

              {otherBusy && (
                <div class="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-700 dark:text-amber-300">
                  Já tem um simulado sendo gerado em outro caderno. Espere
                  terminar (ou interrompa pelo aviso flutuante) antes de
                  começar outro.
                </div>
              )}

              <div class="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
                A geração leva de 30s a 2 minutos. <strong>Você pode fechar
                esta janela e continuar usando o app</strong> — a geração
                segue rodando e avisamos no <strong>sininho de
                notificações</strong> quando ficar pronto. Só não feche a
                aba do navegador até terminar.
              </div>
            </div>
          ) : myTask.status === "generating" ? (
            <div class="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <Loader2 className="size-10 animate-spin text-violet-500" />
              <p class="text-sm font-medium">Gerando seu simulado…</p>
              {(typeof myTask.delivered === "number" && myTask.delivered > 0) ||
              myTask.progress ? (
                <p class="text-[13px] font-semibold text-violet-600 dark:text-violet-300">
                  {typeof myTask.delivered === "number" && myTask.delivered > 0 && (
                    <>
                      {myTask.delivered}{" "}
                      {myTask.delivered === 1 ? "questão gerada" : "questões geradas"}
                    </>
                  )}
                  {myTask.progress && (
                    <span class="text-muted-foreground">
                      {typeof myTask.delivered === "number" && myTask.delivered > 0
                        ? ` · ${myTask.progress}`
                        : myTask.progress}
                    </span>
                  )}
                </p>
              ) : null}
              <p class="max-w-md text-[11px] text-muted-foreground">
                A IA está mapeando os tópicos do seu material e criando questões
                que cobrem cada um — em vários lotes, pra não perder nada. Pode
                levar de 1 a alguns minutos.
              </p>
              <p class="max-w-md text-[11px] text-muted-foreground">
                <strong>Pode fechar esta janela</strong> e continuar no app —
                a geração não para. Avisamos no <strong>sininho</strong>
                quando terminar. (Não feche a aba do navegador.)
              </p>
              <p class="text-[10px] text-muted-foreground">
                Ou <strong>interrompa</strong> a qualquer momento no botão
                abaixo.
              </p>
            </div>
          ) : myTask.status === "done" && myTask.result ? (
            <div class="space-y-3 text-center">
              <Sparkles className="mx-auto size-10 text-emerald-500" />
              <h3 class="text-base font-bold">Simulado pronto!</h3>
              <p class="text-sm">
                <strong>{myTask.result.delivered}</strong>{" "}
                {myTask.result.delivered === 1 ? "questão gerada" : "questões geradas"} a
                partir do seu material.
              </p>
              <p class="text-[11px] text-muted-foreground">
                Banco "{myTask.result.bank.name}" salvo como{" "}
                <strong>privado</strong>. Você pode estudar, fazer prova
                simulada ou compartilhar pelo botão dentro do banco.
              </p>
              {myTask.result.warnings.length > 0 && (
                <div class="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-left text-[11px] text-amber-700 dark:text-amber-300">
                  <strong>Avisos da geração:</strong>
                  <ul class="mt-1 list-inside list-disc space-y-0.5">
                    {myTask.result.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <footer class="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          {myTask?.status === "done" ? (
            <>
              <button
                type="button"
                onClick={() => {
                  dismissBankGen();
                  onClose();
                }}
                class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={openCreated}
                class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-violet-700"
              >
                <Sparkles className="size-3.5" />
                Abrir banco
              </button>
            </>
          ) : myTask?.status === "generating" ? (
            <>
              <button
                type="button"
                onClick={cancelBankGeneration}
                class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
              >
                Interromper
              </button>
              <button
                type="button"
                onClick={close}
                class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-violet-700"
              >
                <Minimize2 className="size-3.5" />
                Fechar (continua gerando)
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={close}
                class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={generate}
                disabled={otherBusy}
                class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-violet-700 disabled:opacity-50"
              >
                <Wand2 className="size-3.5" />
                Gerar simulado
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
